import React from 'react';
import { Modal, Descriptions, Divider, Typography, Space, Button } from 'antd';
import { FileTextOutlined, PrinterOutlined } from '@ant-design/icons';
import type { FacturaPreview } from '../../../api/DTEs/services/autoFacturacion.service';
import { formatCurrency } from '../utils/facturaUtils';

const { Title, Text } = Typography;

interface FacturaPreviewModalProps {
  visible: boolean;
  onClose: () => void;
  preview: FacturaPreview | null;
  onGenerate?: () => void;
  loading?: boolean;
}

export const FacturaPreviewModal: React.FC<FacturaPreviewModalProps> = ({
  visible,
  onClose,
  preview,
  onGenerate,
  loading = false
}) => {
  if (!preview) return null;

  const { factura, resumen } = preview;

  return (
    <Modal
      title={
        <Space>
          <FileTextOutlined />
          <Title level={4} style={{ margin: 0 }}>
            Preview - Factura Electrónica
          </Title>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancelar
        </Button>,
        <Button
          key="generate"
          type="primary"
          icon={<PrinterOutlined />}
          onClick={onGenerate}
          loading={loading}
        >
          {onGenerate ? 'Continuar al Formulario' : 'Generar Factura'}
        </Button>
      ]}
    >
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {/* Resumen de la Factura */}
        <Title level={5}>📋 Resumen de Facturación</Title>
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="Cliente" span={2}>
            <Text strong>{resumen.cliente}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Descripción" span={2}>
            {resumen.descripcion}
          </Descriptions.Item>
          <Descriptions.Item label="Subtotal">
            {formatCurrency(resumen.monto)}
          </Descriptions.Item>
          <Descriptions.Item label="IVA (13%)">
            {formatCurrency(resumen.iva)}
          </Descriptions.Item>
          <Descriptions.Item label="Total" span={2}>
            <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
              {formatCurrency(resumen.total)}
            </Text>
          </Descriptions.Item>
        </Descriptions>

        <Divider />

        {/* Información del Emisor */}
        <Title level={5}>🏢 Información del Emisor</Title>
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="NIT">
            {factura.nit}
          </Descriptions.Item>
          <Descriptions.Item label="Razón Social">
            Clínica Sonríe
          </Descriptions.Item>
        </Descriptions>

        <Divider />

        {/* Información del Receptor */}
        <Title level={5}>👤 Información del Receptor</Title>
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="Nombre">
            {factura.receptor.nombre}
          </Descriptions.Item>
          <Descriptions.Item label="Documento">
            {factura.receptor.numDocumento}
          </Descriptions.Item>
          <Descriptions.Item label="NRC">
            {factura.receptor.nrc}
          </Descriptions.Item>
          <Descriptions.Item label="Teléfono">
            {factura.receptor.telefono}
          </Descriptions.Item>
          <Descriptions.Item label="Email" span={2}>
            {factura.receptor.correo}
          </Descriptions.Item>
          <Descriptions.Item label="Dirección" span={2}>
            {factura.receptor.direccion.complemento}
          </Descriptions.Item>
        </Descriptions>

        <Divider />

        {/* Detalle de Productos/Servicios */}
        <Title level={5}>📦 Detalle de Servicios</Title>
        {factura.cuerpoDocumento.map((item, index) => (
          <Descriptions key={index} bordered size="small" column={2} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Código">
              {item.codigo}
            </Descriptions.Item>
            <Descriptions.Item label="Cantidad">
              {item.cantidad}
            </Descriptions.Item>
            <Descriptions.Item label="Descripción" span={2}>
              {item.descripcion}
            </Descriptions.Item>
            <Descriptions.Item label="Precio Unitario">
              {formatCurrency(item.precioUni)}
            </Descriptions.Item>
            <Descriptions.Item label="IVA">
              {formatCurrency(item.ivaItem)}
            </Descriptions.Item>
            <Descriptions.Item label="Venta Gravada" span={2}>
              <Text strong>{formatCurrency(item.ventaGravada)}</Text>
            </Descriptions.Item>
          </Descriptions>
        ))}

        <Divider />

        {/* Información de Pago */}
        <Title level={5}>💳 Información de Pago</Title>
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="Condición de Operación">
            {factura.resumen.condicionOperacion === 1 ? 'Al Contado' : 'A Crédito'}
          </Descriptions.Item>
          <Descriptions.Item label="Forma de Pago">
            {factura.resumen.pagos[0]?.codigo === "01" ? "Efectivo" : 
             factura.resumen.pagos[0]?.codigo === "03" ? "Tarjeta de Crédito" : "Otro"}
          </Descriptions.Item>
          <Descriptions.Item label="Referencia de Pago" span={2}>
            {factura.resumen.pagos[0]?.referencia}
          </Descriptions.Item>
          {factura.resumen.numPagoElectronico && (
            <Descriptions.Item label="Pago Electrónico" span={2}>
              {factura.resumen.numPagoElectronico}
            </Descriptions.Item>
          )}
        </Descriptions>

        <Divider />

        {/* Información Adicional */}
        <Title level={5}>📝 Información Adicional</Title>
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="Observaciones">
            {factura.extension.observaciones}
          </Descriptions.Item>
        </Descriptions>

        {/* Apéndices */}
        {factura.apendice && factura.apendice.length > 0 && (
          <>
            <Divider />
            <Title level={5}>📎 Información Técnica</Title>
            <Descriptions bordered size="small" column={2}>
              {factura.apendice.map((apendice, index) => (
                <Descriptions.Item key={index} label={apendice.etiqueta}>
                  {apendice.valor}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </>
        )}
      </div>
    </Modal>
  );
};
